import React, { useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, List, ListItem, ListItemText, Tooltip, Typography, alpha,
} from '@mui/material';
import CloudDoneRoundedIcon from '@mui/icons-material/CloudDoneRounded';
import CloudOffRoundedIcon from '@mui/icons-material/CloudOffRounded';
import CloudSyncRoundedIcon from '@mui/icons-material/CloudSyncRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import dayjs from 'dayjs';
import { useOffline } from './useOffline';
import { sincronizar } from './sincronizacao';
import { IItemFila, removerDaFila, usuarioLogado } from './fila';
import { limparSessaoOffline } from './sessaoOffline';
import { dbCount, TStore } from './db';

// Painel de sincronização offline — o mesmo no PDVSIAC e no FrontSIAC (cada
// app só decide onde fica o botão que abre ele): última sincronização,
// quantos registros de cada cadastro estão guardados neste computador,
// lançamentos offline ainda não enviados (com o erro, se o servidor recusou
// algum) e "Sincronizar agora".

const formatarMoeda = (valor: number) =>
  (Number(valor) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const CADASTROS: { store: TStore; nome: string }[] = [
  { store: 'produtos', nome: 'Produtos' },
  { store: 'referencias', nome: 'Referências' },
  { store: 'parceiros', nome: 'Parceiros' },
  { store: 'planocontas', nome: 'Plano de contas' },
  { store: 'operacoes', nome: 'Operações' },
  { store: 'contas', nome: 'Contas' },
  { store: 'vendedores', nome: 'Vendedores' },
  { store: 'usuarios', nome: 'Usuários (login offline)' },
];

export const descreverItemFila = (item: IItemFila) => {
  if (item.tipo === 'abrirsessao') return `Abertura de caixa — ${formatarMoeda(item.extra?.valorabertura)}`;
  if (item.tipo === 'parceiro') return `Novo parceiro — ${item.extra?.nome || ''}`;
  const origem = item.tipo === 'vendafront' ? 'Venda (sistema)' : 'Venda (PDV)';
  return `${origem} — ${formatarMoeda(item.extra?.total)}${item.extra?.nomeParceiro ? ` — ${item.extra.nomeParceiro}` : ''}`;
};

export const IconeConexao: React.FC<{ online: boolean; sincronizando: boolean }> = ({ online, sincronizando }) => {
  if (sincronizando) return <CloudSyncRoundedIcon sx={{ fontSize: 'inherit' }} />;
  if (online) return <CloudDoneRoundedIcon sx={{ fontSize: 'inherit' }} />;
  return <CloudOffRoundedIcon sx={{ fontSize: 'inherit' }} />;
};

// Verde online, amarelo offline, vermelho se algum lançamento foi recusado.
export const corDaConexao = (online: boolean, comErro: number) => (!online ? '#F59E0B' : comErro > 0 ? '#EF4444' : '#22C55E');

interface PainelSincronizacaoProps {
  open: boolean;
  onClose: () => void;
}

export const PainelSincronizacao: React.FC<PainelSincronizacaoProps> = ({ open, onClose }) => {
  const { online, fila, pendentes, comErro, sincronizacao } = useOffline();
  const [contagens, setContagens] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!open || sincronizacao.sincronizando) return;
    Promise.all(CADASTROS.map((c) => dbCount(c.store).catch(() => 0))).then((totais) => {
      setContagens(Object.fromEntries(CADASTROS.map((c, i) => [c.store, totais[i]])));
    });
  }, [open, sincronizacao.sincronizando, sincronizacao.ultimaSincronizacao]);

  const descartar = async (item: IItemFila) => {
    const confirmado = window.confirm(
      `Descartar "${descreverItemFila(item)}"?\n\nEle NÃO será enviado ao servidor. Use só se já tiver sido lançado de outra forma.`,
    );
    if (!confirmado) return;
    await removerDaFila(item.id);
    if (item.tipo === 'abrirsessao') await limparSessaoOffline();
  };

  const cor = corDaConexao(online, comErro);
  const usuarioAtual = usuarioLogado().iduser;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Sincronização offline</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Box sx={{ color: cor, display: 'flex', fontSize: 24 }}>
            <IconeConexao online={online} sincronizando={sincronizacao.sincronizando} />
          </Box>
          <Typography fontWeight={600}>
            {online ? 'Conectado ao servidor' : 'Sem conexão — trabalhando offline'}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary">
          Última sincronização dos cadastros:{' '}
          {sincronizacao.ultimaSincronizacao
            ? dayjs(sincronizacao.ultimaSincronizacao).format('DD/MM/YYYY HH:mm')
            : 'nunca'}
        </Typography>
        {sincronizacao.sincronizando && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2">{sincronizacao.etapa}</Typography>
          </Box>
        )}
        {!sincronizacao.sincronizando && sincronizacao.erro && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>{sincronizacao.erro}</Typography>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 1, mt: 2 }}>
          {CADASTROS.map((c) => (
            <Box key={c.store} sx={{ p: 1, borderRadius: 2, backgroundColor: alpha('#888', 0.1) }}>
              <Typography variant="caption" color="text.secondary">{c.nome}</Typography>
              <Typography fontWeight={700}>{(contagens[c.store] ?? 0).toLocaleString('pt-BR')}</Typography>
            </Box>
          ))}
        </Box>

        <Typography fontWeight={700} sx={{ mt: 2.5, mb: 0.5 }}>
          Lançamentos aguardando envio ({pendentes})
        </Typography>
        {pendentes === 0 ? (
          <Typography variant="body2" color="text.secondary">Nenhum — tudo já foi enviado ao servidor.</Typography>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 280, overflow: 'auto' }}>
            {fila.map((item) => (
              <ListItem
                key={item.id}
                disableGutters
                secondaryAction={item.status === 'erro' ? (
                  <Tooltip title="Descartar (não enviar)">
                    <IconButton edge="end" size="small" onClick={() => descartar(item)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : undefined}
              >
                <ListItemText
                  primary={descreverItemFila(item)}
                  secondary={
                    item.status === 'erro'
                      ? <Typography component="span" variant="caption" color="error">Recusado pelo servidor: {item.erro}</Typography>
                      : item.iduser && item.iduser !== usuarioAtual
                        ? `${dayjs(item.criadoem).format('DD/MM HH:mm')} — aguardando o usuário que lançou entrar neste computador`
                        : `${dayjs(item.criadoem).format('DD/MM HH:mm')} — aguardando envio`
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        <Button
          variant="contained"
          disabled={sincronizacao.sincronizando}
          onClick={() => sincronizar({ cadastros: true })}
        >
          {sincronizacao.sincronizando ? 'Sincronizando...' : 'Sincronizar agora'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
